import { useEffect, useState } from "react";
import { API, useAuth, authFetch } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, TrendingDown, TrendingUp, Package, AlertCircle, Edit2, Check, X } from "lucide-react";

const formatINR = (paise) => {
  const r = (paise || 0) / 100;
  if (r >= 10000000) return `₹${(r / 10000000).toFixed(2)}Cr`;
  if (r >= 100000) return `₹${(r / 100000).toFixed(2)}L`;
  if (r >= 1000) return `₹${(r / 1000).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
};

/**
 * Admin-only page that shows the *true cost* of each employee on a per-bag-sold
 * basis: (monthly_salary + approved_expenses) / bags_sold_this_period.
 *
 * Includes inline editing for monthly_salary so finance can update it without
 * leaving the page.
 */
const CostPerBagPage = () => {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "SALES_ADMIN", "FACTORY_ADMIN", "ACCOUNTS_ADMIN"].includes(user?.role);
  const today = new Date();
  const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [region, setRegion] = useState("ALL");
  const [data, setData] = useState({ data: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [editingSalary, setEditingSalary] = useState(null);
  const [newSalary, setNewSalary] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/analytics/cost-per-bag?period=${period}&region=${region}`);
      const j = await res.json();
      if (j.success) setData({ data: j.data || [], meta: j.meta || {} });
    } catch (e) { toast.error("Failed to load cost analytics"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, region]);

  const saveSalary = async (employeeId) => {
    const value = parseFloat(newSalary);
    if (isNaN(value) || value < 0) { toast.error("Enter a valid amount"); return; }
    // Persist salary in paise — UI takes rupees
    const paise = Math.round(value * 100);
    try {
      const res = await authFetch(`${API}/employees/${employeeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monthly_salary: paise }),
      });
      const j = await res.json();
      if (j.success) {
        toast.success("Salary updated");
        setEditingSalary(null);
        load();
      } else toast.error(j.detail || "Update failed");
    } catch { toast.error("Update failed"); }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="p-12 text-center text-slate-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Admin access required</p>
        </Card>
      </div>
    );
  }

  const summary = data.meta?.summary || {};

  return (
    <div className="p-6 space-y-5" data-testid="cost-per-bag-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-violet-600" /> Cost-per-Bag Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            True cost per bag sold = (monthly salary + approved expenses) ÷ bags dispatched.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40 h-9"
            data-testid="period-picker"
          />
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-32 h-9" data-testid="region-picker"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All regions</SelectItem>
              <SelectItem value="RGN-NORTH">North</SelectItem>
              <SelectItem value="RGN-SOUTH">South</SelectItem>
              <SelectItem value="RGN-EAST">East</SelectItem>
              <SelectItem value="RGN-WEST">West</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cost-summary">
        <Card className="p-3 border-l-4 border-violet-500">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Total cost</p>
          <p className="text-lg font-bold text-violet-700 font-mono mt-1">{formatINR((summary.total_salary || 0) + (summary.total_expenses || 0))}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{summary.employees || 0} employees</p>
        </Card>
        <Card className="p-3 border-l-4 border-amber-500">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Bags sold</p>
          <p className="text-lg font-bold text-amber-700 font-mono mt-1">{(summary.total_bags || 0).toLocaleString("en-IN")}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Period {period}</p>
        </Card>
        <Card className="p-3 border-l-4 border-blue-500">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Revenue</p>
          <p className="text-lg font-bold text-blue-700 font-mono mt-1">{formatINR(summary.total_revenue || 0)}</p>
        </Card>
        <Card className="p-3 border-l-4 border-emerald-500">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Avg cost/bag</p>
          <p className="text-lg font-bold text-emerald-700 font-mono mt-1">
            {summary.avg_cost_per_bag != null ? `₹${(summary.avg_cost_per_bag / 100).toFixed(2)}` : "—"}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Across team</p>
        </Card>
        <Card className="p-3 border-l-4 border-slate-500">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Avg revenue/bag</p>
          <p className="text-lg font-bold text-slate-700 font-mono mt-1">
            {summary.avg_revenue_per_bag != null ? `₹${(summary.avg_revenue_per_bag / 100).toFixed(2)}` : "—"}
          </p>
        </Card>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cost-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr className="border-b">
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-right px-3 py-2">Salary/mo</th>
                <th className="text-right px-3 py-2">Expenses</th>
                <th className="text-right px-3 py-2">Total cost</th>
                <th className="text-right px-3 py-2">Bags sold</th>
                <th className="text-right px-3 py-2">Revenue</th>
                <th className="text-right px-3 py-2">Cost/bag</th>
                <th className="text-right px-3 py-2">Margin/bag</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading...</td></tr>
              ) : data.data.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">No employees in this region</td></tr>
              ) : data.data.map((r) => {
                const margin = r.margin_per_bag;
                const marginColor = margin == null ? "text-slate-400" : margin > 0 ? "text-emerald-700" : "text-red-700";
                return (
                  <tr key={r.employee_id} className="border-b last:border-0 hover:bg-slate-50/50" data-testid={`cost-row-${r.employee_id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900">{r.full_name}</p>
                      <p className="text-[10px] text-slate-400">{r.employee_code} · {r.region_id}</p>
                    </td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.role}</Badge></td>
                    <td className="px-3 py-2 text-right font-mono">
                      {editingSalary === r.employee_id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <Input
                            type="number"
                            min="0"
                            value={newSalary}
                            onChange={(e) => setNewSalary(e.target.value)}
                            placeholder="₹"
                            className="h-7 w-24 text-right"
                            data-testid={`salary-input-${r.employee_id}`}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === "Enter") saveSalary(r.employee_id); if (e.key === "Escape") setEditingSalary(null); }}
                          />
                          <button onClick={() => saveSalary(r.employee_id)} className="text-emerald-600 p-0.5 hover:bg-emerald-50 rounded" data-testid={`save-salary-${r.employee_id}`}>
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={() => setEditingSalary(null)} className="text-slate-400 p-0.5 hover:bg-slate-100 rounded">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setEditingSalary(r.employee_id); setNewSalary(String((r.monthly_salary || 0) / 100)); }}
                          className="hover:underline inline-flex items-center gap-1"
                          data-testid={`edit-salary-${r.employee_id}`}
                          title="Click to edit salary"
                        >
                          {r.monthly_salary > 0 ? formatINR(r.monthly_salary) : <span className="text-amber-600 italic">Not set</span>}
                          <Edit2 className="w-2.5 h-2.5 text-slate-400" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.expenses_total > 0 ? formatINR(r.expenses_total) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{r.total_cost > 0 ? formatINR(r.total_cost) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.bags_sold > 0 ? r.bags_sold.toLocaleString("en-IN") : <Package className="w-3 h-3 inline text-slate-300" />}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.revenue > 0 ? formatINR(r.revenue) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {r.cost_per_bag != null ? `₹${(r.cost_per_bag / 100).toFixed(2)}` : <span className="text-slate-300">N/A</span>}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${marginColor}`}>
                      {margin != null ? (
                        <span className="inline-flex items-center gap-0.5 justify-end">
                          {margin > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          ₹{(margin / 100).toFixed(2)}
                        </span>
                      ) : <span className="text-slate-300">N/A</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-slate-400">
        Tip: click any salary cell to edit it. Margin/bag = revenue/bag − cost/bag. Negative margin means you're paying more than the bags earn — usually due to low bag volume.
      </p>
    </div>
  );
};

export default CostPerBagPage;
